import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import { useNamespace } from "../../hooks/useNamespace";
import { useAuth } from "../../contexts/AuthContext";
import { getInvoice, deleteInvoice } from "../../api/invoiceService";
import { formatDate } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import "../../styles/invoice-print.css";

// Print-ready invoice. Layout matches the reference template
// (D:\Downloads\Smart CA invoice.pdf): Tax Invoice heading, 2-col seller/buyer
// box, line-items table with CGST/SGST/IGST columns, HSN/SAC tax-summary,
// bank details + declaration + signatory blocks. The "Print" button calls
// `window.print()` — the browser print dialog handles "Save as PDF" too.
export default function InvoiceViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ns = useNamespace();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("payments.edit");
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getInvoice(id)
      .then((res) => setInvoice(res?.data))
      .catch((err) => showApiError(err, "Failed to load invoice"))
      .finally(() => setLoading(false));
  }, [id]);

  const remove = async () => {
    if (!window.confirm(
      "Delete this invoice? Invoice numbers are an integer sequence — you can re-issue with the same number afterwards if needed.",
    )) return;

    try {
      await deleteInvoice(id);
      showToast("success", "Invoice deleted");
      navigate(`${ns}/invoices`, { replace: true });
    } catch (err) {
      showApiError(err);
    }
  };

  if (loading || !invoice) {
    return <div className="card"><div className="card-body"><LoadingBlock rows={6} /></div></div>;
  }

  // HSN/SAC summary intentionally excludes lines with no tax — pass-through
  // expense lines (apply_gst=false) carry taxable but zero CGST/SGST/IGST,
  // so they don't belong in the *tax* summary table even though they show up
  // in the line-items table above.
  const linesByHsn = groupLinesByHsn(invoice).filter((row) => row.totalTax > 0);
  // Visible-row taxable total — different from invoice.taxable_total when
  // zero-tax rows are hidden. Tax totals stay invoice-level (hidden rows
  // contribute 0 to them anyway).
  const summaryTaxableTotal = linesByHsn.reduce((sum, r) => sum + r.taxable, 0);
  const interState = !!invoice.inter_state;
  const isGst = invoice.gst_treatment === "gst";

  return (
    <>
      {/* Page-head — hidden from print via .no-print */}
      <div className="page-head no-print">
        <div>
          <Link to={`${ns}/invoices`} className="t-muted text-xs">
            <Icon name="arrow-left" size={11} /> Back to Invoices
          </Link>
          <h1 className="page-title">Invoice {invoice.invoice_no}</h1>
          <div className="page-sub">
            {formatDate(invoice.invoice_date)} · {invoice.buyer_name}
            {invoice.creator?.name && <> · by <strong>{invoice.creator.name}</strong></>}
          </div>
        </div>
        <div className="page-actions">
          {canEdit && (
            <button className="btn btn-secondary" style={{ color: "var(--danger)" }} onClick={remove}>
              <Icon name="trash" size={13} /> Delete
            </button>
          )}
          {canEdit && (
            <Link to={`${ns}/invoices/${id}/edit`} className="btn btn-secondary">
              <Icon name="edit" size={13} /> Edit
            </Link>
          )}
          <button className="btn btn-primary" onClick={() => window.print()}>
            <Icon name="printer" size={14} /> Print
          </button>
        </div>
      </div>

      <div className="invoice-print">
        {/* "Tax Invoice" is reserved for GST-mode invoices (it has tax
            implications under GST rules); non-GST docs print as plain
            "Invoice" instead. */}
        <div className="invoice-title">{isGst ? "Tax Invoice" : "Invoice"}</div>

        {/* Header: Invoice No + Date row */}
        <table className="ip-table ip-header">
          <tbody>
            <tr>
              <td style={{ width: "50%" }}>
                <div className="ip-label">Invoice No.</div>
                <div className="ip-strong">{invoice.invoice_no}</div>
              </td>
              <td>
                <div className="ip-label">Dated</div>
                <div className="ip-strong">{formatDate(invoice.invoice_date)}</div>
              </td>
            </tr>
            <tr>
              {/* Seller */}
              <td>
                <div className="ip-strong">{invoice.firm_name}</div>
                {invoice.firm_address && (
                  <div className="ip-multiline">{invoice.firm_address}</div>
                )}
                {/* GSTIN is GST-mode-only — irrelevant on a plain Invoice. */}
                {isGst && invoice.firm_gstin && <div>GSTIN/UIN: <strong>{invoice.firm_gstin}</strong></div>}
                {invoice.firm_state_name && (
                  <div>State Name: <strong>{invoice.firm_state_name}</strong>{invoice.firm_state_code ? `, Code : ${invoice.firm_state_code}` : ""}</div>
                )}
              </td>
              {/* Buyer */}
              <td>
                <div className="ip-label">Buyer (Bill to)</div>
                <div className="ip-strong">{invoice.buyer_name}</div>
                {/* Client ID + phone are useful on every printed invoice for
                    downstream reconciliation; not gated on GST mode. */}
                {invoice.buyer_client_id && (
                  <div>Client ID: <strong>{invoice.buyer_client_id}</strong></div>
                )}
                {invoice.buyer_phone && (
                  <div>Mobile: <strong>{invoice.buyer_phone}</strong></div>
                )}
                {invoice.buyer_address && (
                  <div className="ip-multiline">{invoice.buyer_address}</div>
                )}
                {isGst && invoice.buyer_gstin && <div>GSTIN/UIN: <strong>{invoice.buyer_gstin}</strong></div>}
                {invoice.buyer_state_name && (
                  <div>State Name: <strong>{invoice.buyer_state_name}</strong>{invoice.buyer_state_code ? `, Code : ${invoice.buyer_state_code}` : ""}</div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Line items table — Qty/Rate were replaced by a Taxable column
            (more useful for tax invoices than blank Qty/Rate cells).
            HSN/SAC hidden on no-GST invoices; per-line apply_gst=false means
            the line passes through untaxed (taxable_value = amount). */}
        {(() => {
          // Visible cols: Sl, Description, [HSN], [Taxable], [CGST, SGST | IGST], Amount.
          const visibleCols = 2 // Sl + Desc
            + (isGst ? 1 : 0)                 // HSN/SAC
            + (isGst ? 1 : 0)                 // Taxable
            + (isGst && !interState ? 2 : 0)  // CGST + SGST
            + (isGst &&  interState ? 1 : 0)  // IGST
            + 1;                              // Amount
          return (
            <table className="ip-table ip-lines">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>Sl No.</th>
                  <th>Description of Services</th>
                  {isGst && <th style={{ width: 80 }}>HSN/SAC</th>}
                  {isGst && <th style={{ width: 100 }}>Taxable</th>}
                  {isGst && !interState && <th style={{ width: 80 }}>CGST {fmtRate(invoice.cgst_rate)}%</th>}
                  {isGst && !interState && <th style={{ width: 80 }}>SGST {fmtRate(invoice.sgst_rate)}%</th>}
                  {isGst && interState && <th style={{ width: 80 }}>IGST {fmtRate(invoice.igst_rate)}%</th>}
                  <th style={{ width: 100 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines?.map((l, i) => (
                  <tr key={l.id || i}>
                    <td className="t-num">{l.position || i + 1}</td>
                    <td><strong>{l.description}</strong></td>
                    {isGst && <td className="t-mono">{l.hsn_code || ""}</td>}
                    {isGst && <td className="t-num">{fmtMoney(l.taxable_value)}</td>}
                    {isGst && !interState && <td className="t-num">{fmtMoney(l.cgst_amount)}</td>}
                    {isGst && !interState && <td className="t-num">{fmtMoney(l.sgst_amount)}</td>}
                    {isGst && interState && <td className="t-num">{fmtMoney(l.igst_amount)}</td>}
                    <td className="t-num"><strong>{fmtMoney(l.amount)}</strong></td>
                  </tr>
                ))}
                {/* Total row */}
                <tr className="ip-total-row">
                  <td colSpan={visibleCols - 1} style={{ textAlign: "right" }}><strong>Total</strong></td>
                  <td className="t-num">
                    <strong>₹ {fmtMoney(invoice.grand_total)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          );
        })()}

        {/* Amount in words */}
        <div className="ip-words">
          Amount Chargeable (in words) <span className="ip-eoe">E. & O.E</span>
        </div>
        <div className="ip-words-strong">{invoice.amount_in_words}</div>

        {/* HSN/SAC tax breakdown — only on GST invoices */}
        {isGst && (
          <table className="ip-table ip-summary">
            <thead>
              <tr>
                <th rowSpan={2}>HSN/SAC</th>
                <th rowSpan={2}>Taxable Value</th>
                {!interState && <th colSpan={2}>CGST</th>}
                {!interState && <th colSpan={2}>SGST/UTGST</th>}
                {interState && <th colSpan={2}>IGST</th>}
                <th rowSpan={2}>Total Tax Amount</th>
              </tr>
              <tr>
                <th>Rate</th><th>Amount</th>
                {!interState && (<><th>Rate</th><th>Amount</th></>)}
              </tr>
            </thead>
            <tbody>
              {linesByHsn.map((row) => (
                <tr key={row.hsn || "—"}>
                  <td className="t-mono">{row.hsn || "—"}</td>
                  <td className="t-num">{fmtMoney(row.taxable)}</td>
                  {!interState && <td className="t-num">{fmtRate(invoice.cgst_rate)}%</td>}
                  {!interState && <td className="t-num">{fmtMoney(row.cgst)}</td>}
                  {!interState && <td className="t-num">{fmtRate(invoice.sgst_rate)}%</td>}
                  {!interState && <td className="t-num">{fmtMoney(row.sgst)}</td>}
                  {interState && <td className="t-num">{fmtRate(invoice.igst_rate)}%</td>}
                  {interState && <td className="t-num">{fmtMoney(row.igst)}</td>}
                  <td className="t-num">{fmtMoney(row.totalTax)}</td>
                </tr>
              ))}
              <tr className="ip-total-row">
                <td><strong>Total</strong></td>
                <td className="t-num"><strong>{fmtMoney(summaryTaxableTotal)}</strong></td>
                {!interState && <td></td>}
                {!interState && <td className="t-num"><strong>{fmtMoney(invoice.cgst_amount)}</strong></td>}
                {!interState && <td></td>}
                {!interState && <td className="t-num"><strong>{fmtMoney(invoice.sgst_amount)}</strong></td>}
                {interState && <td></td>}
                {interState && <td className="t-num"><strong>{fmtMoney(invoice.igst_amount)}</strong></td>}
                <td className="t-num">
                  <strong>{fmtMoney(
                    Number(invoice.cgst_amount) + Number(invoice.sgst_amount) + Number(invoice.igst_amount),
                  )}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {isGst && (
          <div className="ip-words">Tax Amount (in words): <strong>{invoice.tax_in_words}</strong></div>
        )}

        {/* Bank details + PAN + Declaration + Signatory */}
        <table className="ip-table ip-foot">
          <tbody>
            <tr>
              <td style={{ width: "50%", verticalAlign: "top" }}>
                {invoice.firm_pan && <div>Company's PAN : <strong>{invoice.firm_pan}</strong></div>}
                {invoice.declaration_text && (
                  <>
                    <div style={{ marginTop: 10 }}><u>Declaration</u></div>
                    <div className="ip-multiline">{invoice.declaration_text}</div>
                  </>
                )}
              </td>
              <td style={{ verticalAlign: "top" }}>
                <div><strong>Company's Bank Details</strong></div>
                {invoice.firm_bank_name && <div>Bank Name : <strong>{invoice.firm_bank_name}</strong></div>}
                {invoice.firm_bank_account_no && <div>A/c No. : <strong>{invoice.firm_bank_account_no}</strong></div>}
                {(invoice.firm_bank_branch || invoice.firm_bank_ifsc) && (
                  <div>
                    Branch &amp; IFS Code: <strong>
                      {[invoice.firm_bank_branch, invoice.firm_bank_ifsc].filter(Boolean).join(" & ")}
                    </strong>
                  </div>
                )}
                <div style={{ marginTop: 24 }}>for <strong>{invoice.firm_name}</strong></div>
                <div style={{ marginTop: 28, textAlign: "right" }}>
                  {invoice.signatory_name ? <div>{invoice.signatory_name}</div> : null}
                  <div>Authorised Signatory</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="ip-footer">This is a Computer Generated Invoice</div>
      </div>
    </>
  );
}

function fmtMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtRate(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// Combine lines by HSN/SAC for the rate-summary table. Lines with no HSN
// roll into a single "—" group.
function groupLinesByHsn(invoice) {
  const groups = new Map();
  for (const l of invoice.lines || []) {
    const hsn = l.hsn_code || "";
    if (!groups.has(hsn)) {
      groups.set(hsn, { hsn, taxable: 0, cgst: 0, sgst: 0, igst: 0, totalTax: 0 });
    }
    const g = groups.get(hsn);
    g.taxable += Number(l.taxable_value || 0);
    g.cgst    += Number(l.cgst_amount || 0);
    g.sgst    += Number(l.sgst_amount || 0);
    g.igst    += Number(l.igst_amount || 0);
    g.totalTax += Number(l.cgst_amount || 0) + Number(l.sgst_amount || 0) + Number(l.igst_amount || 0);
  }
  return Array.from(groups.values());
}
